/**
 * Document Dialogs Component
 * Upload, Preview, Edit, Delete confirmation, and Password prompt dialogs
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Download, Lock, Eye, EyeOff, Loader2, ExternalLink } from 'lucide-react';

// Generate month options for the edit dialog
const getMonthOptions = () => {
  const options = [];
  const currentYear = new Date().getFullYear();
  for (let year = currentYear - 2; year <= currentYear + 1; year++) {
    for (let month = 1; month <= 12; month++) {
      const monthStr = `${year}-${String(month).padStart(2, '0')}`;
      const label = format(new Date(year, month - 1, 1), 'MMMM yyyy');
      options.push({ value: monthStr, label });
    }
  }
  return options;
};

// Upload Dialog with password support
export const UploadDialog = ({ 
  open, 
  onOpenChange, 
  uploadMonth, 
  uploadLoading, 
  fileInputRef, 
  onFileUpload,
  pdfPassword,
  onPasswordChange
}) => {
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            Upload Wage Slip for {uploadMonth ? format(new Date(uploadMonth + '-01'), 'MMMM yyyy') : ''} Earnings
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* PDF Password field */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-slate-400" />
              <Label className="text-slate-300">PDF Password (if protected)</Label>
            </div>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={pdfPassword || ''}
                onChange={(e) => onPasswordChange?.(e.target.value)}
                placeholder="Leave blank if not password protected"
                className="bg-slate-700/50 border-slate-600 text-white pr-10"
                data-testid="upload-pdf-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Some employers password-protect wage slips (e.g., date of birth)
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Select File</Label>
            <Input
              ref={fileInputRef}
              type="file"
              onChange={onFileUpload}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              className="bg-slate-900 border-slate-600 text-white file:bg-slate-700 file:text-white file:border-0"
              disabled={uploadLoading}
            />
            <p className="text-xs text-slate-500">PDF files will be automatically analyzed to extract earnings data for comparison</p>
            <p className="text-xs text-amber-400/80">Note: Wage slips are typically received the month after earnings (e.g., January payslip = December earnings)</p>
          </div>
          {uploadLoading && (
            <div className="flex items-center justify-center py-4 gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="text-slate-400 text-sm">Uploading and extracting data...</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Preview Dialog — fetches raw file bytes for reliable rendering
export const PreviewDialog = ({ previewDoc, onClose, onDownload, onPasswordSaved }) => {
  const [blobUrl, setBlobUrl] = React.useState(null);

  React.useEffect(() => {
    if (!previewDoc?.id) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      return;
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewDoc.id}/preview`;
    axios.get(url, { responseType: 'blob' })
      .then(res => setBlobUrl(URL.createObjectURL(res.data)))
      .catch(() => setBlobUrl(null));

    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDoc?.id]);

  return (
    <Dialog open={!!previewDoc} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 w-[95vw] max-w-5xl h-[85vh] flex flex-col p-0" data-testid="earnings-preview-modal">
        <DialogHeader className="px-4 py-3 border-b border-slate-700 flex-shrink-0 pr-14">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-white text-sm truncate pr-4">{previewDoc?.file_name}</DialogTitle>
            <div className="flex items-center gap-2 flex-shrink-0">
              {blobUrl && (
                <a href={blobUrl} download={previewDoc?.file_name}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors">
                  <Download className="h-3.5 w-3.5" />Download
                </a>
              )}
              {blobUrl && (
                <a href={blobUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors">
                  <ExternalLink className="h-3.5 w-3.5" />New Tab
                </a>
              )}
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-slate-900">
          {blobUrl ? (
            (previewDoc?.file_name?.toLowerCase().endsWith('.pdf') || previewDoc?.content_type === 'application/pdf') ? (
              <div className="w-full h-full flex flex-col">
                <object data={blobUrl} type="application/pdf" className="w-full flex-1">
                  <div className="flex flex-col items-center justify-center h-full gap-4 p-6">
                    <FileText className="h-16 w-16 text-slate-600" />
                    <p className="text-slate-400 text-center">PDF preview not supported on this device</p>
                    <div className="flex gap-3">
                      <a href={blobUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-colors">
                        <ExternalLink className="h-4 w-4" />Open PDF
                      </a>
                      <a href={blobUrl} download={previewDoc?.file_name}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors">
                        <Download className="h-4 w-4" />Download
                      </a>
                    </div>
                  </div>
                </object>
              </div>
            ) : (previewDoc?.file_name?.toLowerCase().match(/\.(png|jpg|jpeg|gif|webp)$/) || (previewDoc?.content_type || '').startsWith('image/')) ? (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img src={blobUrl} alt={previewDoc?.file_name} className="max-w-full max-h-full object-contain rounded" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                <FileText className="h-16 w-16 text-slate-600" />
                <p className="text-slate-400">Preview not available for this file type</p>
                <Button variant="outline" onClick={() => onDownload(previewDoc?.id, previewDoc?.file_name)}>
                  <Download className="h-4 w-4 mr-2" />Download to view
                </Button>
              </div>
            )
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edit Document Dialog
export const EditDialog = ({ editingDoc, setEditingDoc, onSave }) => (
  <Dialog open={!!editingDoc} onOpenChange={() => setEditingDoc(null)}>
    <DialogContent className="bg-slate-800 border-slate-700">
      <DialogHeader>
        <DialogTitle className="text-white">Edit Document</DialogTitle>
      </DialogHeader>
      {editingDoc && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">File Name</Label>
            <Input
              value={editingDoc.file_name}
              onChange={(e) => setEditingDoc({ ...editingDoc, file_name: e.target.value })}
              className="bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Earnings Month</Label>
            <Select
              value={editingDoc.month || 'none'}
              onValueChange={(value) => setEditingDoc({ ...editingDoc, month: value === 'none' ? null : value })}
            >
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 max-h-60">
                <SelectItem value="none" className="text-slate-400">Not assigned</SelectItem>
                {getMonthOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-700">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Document Type</Label>
            <Select
              value={editingDoc.document_type || 'wage_slip'}
              onValueChange={(value) => setEditingDoc({ ...editingDoc, document_type: value })}
            >
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="wage_slip" className="text-white">Wage Slip</SelectItem>
                <SelectItem value="p60" className="text-white">P60</SelectItem>
                <SelectItem value="p45" className="text-white">P45</SelectItem>
                <SelectItem value="p11d" className="text-white">P11D</SelectItem>
                <SelectItem value="flight_stub" className="text-white">Flight Stub</SelectItem>
                <SelectItem value="contract" className="text-white">Contract</SelectItem>
                <SelectItem value="other" className="text-white">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => setEditingDoc(null)} className="border-slate-600 text-slate-300">
          Cancel
        </Button>
        <Button onClick={onSave} className="bg-amber-600 hover:bg-amber-700 text-white">
          Save Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Delete Confirmation Dialog
export const DeleteDialog = ({ deleteConfirmDoc, setDeleteConfirmDoc, onDelete }) => (
  <Dialog open={!!deleteConfirmDoc} onOpenChange={() => setDeleteConfirmDoc(null)}>
    <DialogContent className="bg-slate-800 border-slate-700">
      <DialogHeader>
        <DialogTitle className="text-white">Delete Document</DialogTitle>
      </DialogHeader>
      <div className="py-4">
        <p className="text-slate-300">
          Are you sure you want to delete <span className="font-medium text-white">{deleteConfirmDoc?.file_name}</span>?
        </p>
        <p className="text-sm text-slate-500 mt-2">This action cannot be undone.</p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setDeleteConfirmDoc(null)} className="border-slate-600 text-slate-300">
          Cancel
        </Button>
        <Button 
          onClick={() => onDelete(deleteConfirmDoc?.id)} 
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Password Prompt Dialog for protected PDFs
export const PasswordPromptDialog = ({ 
  open, 
  onClose, 
  documentName,
  documentId,
  onSubmit,
  loading,
  mode = 'extract'
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savePassword, setSavePassword] = useState(true);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (password.trim()) {
      onSubmit(password, savePassword);
      setPassword('');
    }
  };

  const handleClose = () => {
    setPassword('');
    setShowPassword(false);
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-400" />
            Password Required
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {mode === 'save' 
              ? 'Save the PDF password so you and your accountant can open this document without re-entering it.'
              : 'This PDF is password-protected. Enter the password to unlock it.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="p-3 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-300 truncate">{documentName}</p>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-300">PDF Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter PDF password"
                className="bg-slate-700/50 border-slate-600 text-white pr-10"
                autoFocus
                data-testid="pdf-password-prompt-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer" data-testid="save-password-checkbox">
            <input
              type="checkbox"
              checked={savePassword}
              onChange={(e) => setSavePassword(e.target.checked)}
              className="rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
            />
            <span className="text-sm text-slate-300">Remember password for this document</span>
          </label>
          <p className="text-[11px] text-slate-500 -mt-2 ml-6">
            Your accountant will also be able to view this document without needing the password.
          </p>
        
          <DialogFooter className="gap-2">
            <Button 
              type="button"
              variant="outline" 
              onClick={handleClose}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              className="bg-amber-600 hover:bg-amber-700"
              disabled={!password.trim() || loading}
              data-testid="submit-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'save' ? 'Saving...' : 'Extracting...'}
                </>
              ) : (
                mode === 'save' ? 'Save Password' : 'Unlock & Extract'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
